import React, { useState, useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import CircularProgress from '@material-ui/core/CircularProgress'
import Link from '@material-ui/core/Link'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import ScheduleIcon from '@material-ui/icons/Schedule'
import useStyles from './styles'
import {
  HOLE_IN_ONE_BY_CLAIM_TOKEN,
  REQUEST_GOLFER_OTP,
  CLAIM_HOLE_IN_ONE,
} from './query'
import useAchievementsAuth from 'lib/achievements-auth/useAchievementsAuth'
import { decodeToken } from 'lib/achievements-auth/token'
import pgaLogo from 'images/pga-logo-navy.png'

const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const AchievementsClaimPage = () => {
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const { login } = useAchievementsAuth()

  const searchParams = new URLSearchParams(location.search)
  const claimToken = searchParams.get('token')
  const emailVerified = searchParams.get('verified') === 'true'

  const [legalAgreementsAccepted, setLegalAgreementsAccepted] = useState(false)
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [golferEmail, setGolferEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [validationError, setValidationError] = useState('')
  const [otpSent, setOtpSent] = useState(false)

  const { data, loading, error } = useQuery(HOLE_IN_ONE_BY_CLAIM_TOKEN, {
    variables: { claimToken },
    skip: !claimToken,
    context: { skipAuth: true },
  })

  const [requestOtp, { loading: otpLoading }] = useMutation(
    REQUEST_GOLFER_OTP,
    {
      onCompleted: () => {
        setOtpSent(true)
      },
      onError: err => {
        setValidationError(err.message || 'Failed to send verification email.')
      },
    },
  )

  const [claimHoleInOne, { loading: claimLoading }] = useMutation(
    CLAIM_HOLE_IN_ONE,
    {
      onCompleted: response => {
        const { token, holeInOne, errors } = response.claimHoleInOne

        if (errors && errors.length > 0) {
          setValidationError(errors.join(', '))
          return
        }

        if (token && holeInOne) {
          const decoded = decodeToken(token)
          const user = {
            email: decoded?.golfer_email,
            name: decoded?.golfer_name,
          }

          login(token, user)
          history.push(`/achievements/${holeInOne.id}`)
        }
      },
      onError: err => {
        setValidationError(err.message || 'Failed to claim achievement.')
      },
    },
  )

  useEffect(() => {
    if (data?.holeInOneByClaimToken?.legalAgreementsAcceptedAt) {
      history.push(`/achievements/${data.holeInOneByClaimToken.id}`)
    }
  }, [data, history])

  const holeInOne = data?.holeInOneByClaimToken
  const hasEmailOnRecord = Boolean(holeInOne?.maskedEmail)

  const handleRequestOtp = async () => {
    setValidationError('')
    await requestOtp({
      variables: {
        input: {
          claimToken,
          legalAgreementsAccepted,
          marketingOptIn,
        },
      },
      context: { skipAuth: true },
    })
  }

  const handleClaimSubmit = async e => {
    e.preventDefault()
    setValidationError('')

    if (!legalAgreementsAccepted) {
      setValidationError('You must accept the legal agreements to continue.')
      return
    }

    const input = {
      claimToken,
      legalAgreementsAccepted,
      marketingOptIn,
    }

    if (!hasEmailOnRecord && golferEmail) {
      input.golferEmail = golferEmail
    }

    await claimHoleInOne({
      variables: { input },
      context: { skipAuth: true },
    })
  }

  const isLoading = otpLoading || claimLoading

  const renderAchievementPreview = () => {
    if (!holeInOne) return null

    return (
      <Box className={classes.achievementPreview}>
        <Typography className={classes.achievementPreviewTitle}>
          Your Hole-in-One Achievement
        </Typography>
        <Box className={classes.achievementPreviewGrid}>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Course
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.courseName || 'N/A'}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Date
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {formatDate(holeInOne.occurredAt)}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Hole
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              #{holeInOne.holeNumber}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Par
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.holePar}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Distance
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.yardage} Yds
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderConsentForm = () => (
    <form onSubmit={handleClaimSubmit}>
      <Typography className={classes.sectionLabel}>
        CONSENTS & AGREEMENTS
      </Typography>
      <Box className={classes.consentSection}>
        <Box className={classes.consentBox}>
          <FormControlLabel
            className={classes.consentCheckbox}
            control={
              <Checkbox
                checked={legalAgreementsAccepted}
                onChange={e => {
                  setLegalAgreementsAccepted(e.target.checked)
                  if (e.target.checked) {
                    setValidationError('')
                  }
                }}
                className={classes.checkbox}
              />
            }
            label={
              <Box className={classes.consentLabelContainer}>
                <Typography className={classes.consentTitle}>
                  Legal Agreements
                  <span className={classes.requiredAsterisk}>*</span>
                </Typography>
                <Typography className={classes.consentText}>
                  By checking this box, you are agreeing to PGA of America's{' '}
                  <Link
                    href="https://www.pga.com/pga-of-america/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.consentLink}
                  >
                    Terms of Service
                  </Link>
                  , and{' '}
                  <Link
                    href="https://www.pga.com/pga-of-america/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.consentLink}
                  >
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              </Box>
            }
          />
        </Box>

        <Box className={classes.consentBox}>
          <FormControlLabel
            className={classes.consentCheckbox}
            control={
              <Checkbox
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                className={classes.checkbox}
              />
            }
            label={
              <Box className={classes.consentLabelContainer}>
                <Typography className={classes.consentTitle}>
                  Join the PGA of America's email community to hear about local
                  golf programs, ticketing and volunteer opportunities, swing
                  tips, exclusive offers and more! (Optional)
                </Typography>
                <Typography className={classes.consentText}>
                  By checking this box, I confirm my request to be contacted by
                  email by The Professional Golfers' Association of America
                  ("PGA"), PGA Tournament Corporation, Inc. ("PTC") and/or PGA
                  of America partners. I acknowledge that I can withdraw my
                  consent at any time by following the 'unsubscribe'
                  instructions provided on any communication I receive from this
                  service.
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>

      {validationError && (
        <Typography className={classes.validationError}>
          {validationError}
        </Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        className={classes.submitButton}
        disabled={isLoading || !legalAgreementsAccepted}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'CLAIM MY ACHIEVEMENT'
        )}
      </Button>

      <Typography className={classes.helpLink}>
        Need help?{' '}
        <Link href="mailto:support@pga.com" className={classes.supportLink}>
          Contact Support
        </Link>
      </Typography>
    </form>
  )

  const renderOtpSent = () => (
    <Box className={classes.successMessage}>
      <CheckCircleOutlineIcon className={classes.successIcon} />
      <Typography className={classes.successTitle}>Check Your Email</Typography>
      <Typography className={classes.successDescription}>
        We've sent a verification link to complete your claim. Click the link in
        the email to verify your identity and access your achievement.
      </Typography>
      <Typography
        className={classes.successDescription}
        style={{ marginTop: 16 }}
      >
        Didn't receive the email? Check your spam folder or{' '}
        <Link
          href="#"
          onClick={e => {
            e.preventDefault()
            setOtpSent(false)
          }}
          className={classes.supportLink}
        >
          try again
        </Link>
        .
      </Typography>
    </Box>
  )

  const renderMagicLinkFlow = () => {
    if (otpSent) {
      return renderOtpSent()
    }

    if (emailVerified) {
      return (
        <>
          <Typography className={classes.title}>Claim Your Ace</Typography>
          <Typography className={classes.description}>
            Your email has been verified. Please review the details and accept
            the agreements below to claim your achievement.
          </Typography>

          {renderAchievementPreview()}

          <Typography className={classes.sectionLabel}>
            VERIFIED ACCOUNT
          </Typography>
          <Box className={classes.emailVerificationBox}>
            <Avatar className={classes.emailAvatar}>
              <PersonOutlineIcon />
            </Avatar>
            <Typography className={classes.emailText}>
              {holeInOne.maskedEmail}
            </Typography>
            <CheckCircleIcon className={classes.verifiedIcon} />
          </Box>

          {renderConsentForm()}
        </>
      )
    }

    return (
      <>
        <Typography className={classes.title}>Claim Your Ace</Typography>
        <Typography className={classes.description}>
          We found a verified Hole-in-One record associated with your email.
          Accept the agreements below and verify your identity to claim this
          achievement.
        </Typography>

        {renderAchievementPreview()}

        <Typography className={classes.sectionLabel}>
          VERIFYING ACCOUNT FOR
        </Typography>
        <Box className={classes.emailVerificationBox}>
          <Avatar className={classes.emailAvatar}>
            <PersonOutlineIcon />
          </Avatar>
          <Typography className={classes.emailText}>
            {holeInOne.maskedEmail}
          </Typography>
          <ScheduleIcon className={classes.pendingIcon} />
        </Box>

        <Typography className={classes.sectionLabel}>
          CONSENTS & AGREEMENTS
        </Typography>
        <Box className={classes.consentSection}>
          <Box className={classes.consentBox}>
            <FormControlLabel
              className={classes.consentCheckbox}
              control={
                <Checkbox
                  checked={legalAgreementsAccepted}
                  onChange={e => {
                    setLegalAgreementsAccepted(e.target.checked)
                    if (e.target.checked) {
                      setValidationError('')
                    }
                  }}
                  className={classes.checkbox}
                />
              }
              label={
                <Box className={classes.consentLabelContainer}>
                  <Typography className={classes.consentTitle}>
                    Legal Agreements
                    <span className={classes.requiredAsterisk}>*</span>
                  </Typography>
                  <Typography className={classes.consentText}>
                    By checking this box, you are agreeing to PGA of America's{' '}
                    <Link
                      href="https://www.pga.com/pga-of-america/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.consentLink}
                    >
                      Terms of Service
                    </Link>
                    , and{' '}
                    <Link
                      href="https://www.pga.com/pga-of-america/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.consentLink}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box className={classes.consentBox}>
            <FormControlLabel
              className={classes.consentCheckbox}
              control={
                <Checkbox
                  checked={marketingOptIn}
                  onChange={e => setMarketingOptIn(e.target.checked)}
                  className={classes.checkbox}
                />
              }
              label={
                <Box className={classes.consentLabelContainer}>
                  <Typography className={classes.consentTitle}>
                    Join the PGA of America's email community to hear about
                    local golf programs, ticketing and volunteer opportunities,
                    swing tips, exclusive offers and more! (Optional)
                  </Typography>
                  <Typography className={classes.consentText}>
                    By checking this box, I confirm my request to be contacted
                    by email by The Professional Golfers' Association of America
                    ("PGA"), PGA Tournament Corporation, Inc. ("PTC") and/or PGA
                    of America partners. I acknowledge that I can withdraw my
                    consent at any time by following the 'unsubscribe'
                    instructions provided on any communication I receive from
                    this service.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>

        {validationError && (
          <Typography className={classes.validationError}>
            {validationError}
          </Typography>
        )}

        <Button
          variant="contained"
          fullWidth
          className={classes.verifyButton}
          onClick={handleRequestOtp}
          disabled={isLoading || !legalAgreementsAccepted}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'VERIFY EMAIL'
          )}
        </Button>

        <Typography className={classes.helpLink}>
          Need help?{' '}
          <Link href="mailto:support@pga.com" className={classes.supportLink}>
            Contact Support
          </Link>
        </Typography>
      </>
    )
  }

  const renderQRScanFlow = () => {
    const isEmailValid = golferEmail.trim() && isValidEmail(golferEmail)
    const canSubmit = isEmailValid && legalAgreementsAccepted

    return (
      <form onSubmit={handleClaimSubmit}>
        <Typography className={classes.title}>Claim Your Ace</Typography>
        <Typography className={classes.description}>
          A PGA Professional has recorded a Hole-in-One achievement for you.
          Enter your email and accept the agreements below to claim it.
        </Typography>

        {renderAchievementPreview()}

        <Box className={classes.emailInputSection}>
          <Typography className={classes.emailInputLabel}>
            EMAIL ADDRESS
          </Typography>
          <TextField
            type="email"
            placeholder="you@example.com"
            variant="outlined"
            fullWidth
            value={golferEmail}
            onChange={e => {
              setGolferEmail(e.target.value)
              setEmailError('')
            }}
            error={!!emailError}
            helperText={emailError}
            className={classes.emailTextField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon className={classes.emailIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Typography className={classes.sectionLabel}>
          CONSENTS & AGREEMENTS
        </Typography>
        <Box className={classes.consentSection}>
          <Box className={classes.consentBox}>
            <FormControlLabel
              className={classes.consentCheckbox}
              control={
                <Checkbox
                  checked={legalAgreementsAccepted}
                  onChange={e => {
                    setLegalAgreementsAccepted(e.target.checked)
                    if (e.target.checked) {
                      setValidationError('')
                    }
                  }}
                  className={classes.checkbox}
                />
              }
              label={
                <Box className={classes.consentLabelContainer}>
                  <Typography className={classes.consentTitle}>
                    Legal Agreements
                    <span className={classes.requiredAsterisk}>*</span>
                  </Typography>
                  <Typography className={classes.consentText}>
                    By checking this box, you are agreeing to PGA of America's{' '}
                    <Link
                      href="https://www.pga.com/pga-of-america/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.consentLink}
                    >
                      Terms of Service
                    </Link>
                    , and{' '}
                    <Link
                      href="https://www.pga.com/pga-of-america/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.consentLink}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box className={classes.consentBox}>
            <FormControlLabel
              className={classes.consentCheckbox}
              control={
                <Checkbox
                  checked={marketingOptIn}
                  onChange={e => setMarketingOptIn(e.target.checked)}
                  className={classes.checkbox}
                />
              }
              label={
                <Box className={classes.consentLabelContainer}>
                  <Typography className={classes.consentTitle}>
                    Join the PGA of America's email community to hear about
                    local golf programs, ticketing and volunteer opportunities,
                    swing tips, exclusive offers and more! (Optional)
                  </Typography>
                  <Typography className={classes.consentText}>
                    By checking this box, I confirm my request to be contacted
                    by email by The Professional Golfers' Association of America
                    ("PGA"), PGA Tournament Corporation, Inc. ("PTC") and/or PGA
                    of America partners. I acknowledge that I can withdraw my
                    consent at any time by following the 'unsubscribe'
                    instructions provided on any communication I receive from
                    this service.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>

        {validationError && (
          <Typography className={classes.validationError}>
            {validationError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          className={classes.submitButton}
          disabled={isLoading || !canSubmit}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'CLAIM MY ACHIEVEMENT'
          )}
        </Button>

        <Typography className={classes.helpLink}>
          Need help?{' '}
          <Link href="mailto:support@pga.com" className={classes.supportLink}>
            Contact Support
          </Link>
        </Typography>
      </form>
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography className={classes.loadingText}>
            Loading your ace details...
          </Typography>
        </Box>
      )
    }

    if (error || !claimToken || !holeInOne) {
      return (
        <Box className={classes.errorContainer}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography className={classes.errorTitle}>
            {!claimToken ? 'Missing Claim Token' : 'Invalid or Expired Link'}
          </Typography>
          <Typography className={classes.errorDescription}>
            {!claimToken
              ? 'No claim token was provided. Please use the link from your email or scan the QR code again.'
              : 'This claim link may have expired or is invalid. If you believe this is an error, please contact support for assistance.'}
          </Typography>
          <Button
            variant="contained"
            className={classes.errorButton}
            onClick={() => history.push('/achievements/login')}
          >
            GO TO LOGIN
          </Button>
        </Box>
      )
    }

    if (hasEmailOnRecord) {
      return renderMagicLinkFlow()
    } else {
      return renderQRScanFlow()
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.logoContainer}>
        <img src={pgaLogo} alt="PGA of America" className={classes.logo} />
        <Box className={classes.taglineDivider} />
        <Typography className={classes.tagline}>We love this Game</Typography>
      </Box>

      <Box className={classes.card}>{renderContent()}</Box>

      <Typography className={classes.footer}>
        © {new Date().getFullYear()} PGA of America. All rights reserved.
      </Typography>
    </Box>
  )
}

export default AchievementsClaimPage
